import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from '@mui/material'
import {
  socialApi,
  type SocialCancelResponse,
  type SocialPublication,
  type SocialPublicationItem,
} from '@/api/social'
import { scheduledVkPosts, targetLabel } from '@/components/admin/social/socialUi'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { safeHttpUrl } from '@/utils/telegramHtml'

/** Ссылки на отложенные записи VK, которые человек удаляет в VK вручную */
export function VkPostLinks({
  posts,
  publication,
}: {
  posts: SocialPublicationItem[]
  publication: SocialPublication | null
}) {
  const { t } = useTranslation()

  return (
    <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
      {posts.map((post) => {
        // В vk_posts_to_delete цель не подгружена — берётся из шагов публикации
        const target = post.target ?? publication?.items.find((item) => item.id === post.id)?.target
        const label = targetLabel(target, post.social_target_id)
        const url = safeHttpUrl(post.url)

        return (
          <li key={post.id}>
            {url ? (
              <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ overflowWrap: 'anywhere' }}>
                {`${label}: ${url}`}
              </Link>
            ) : (
              t('admin.social.cancelDialog.vkPostWithoutUrl', { target: label, id: post.external_id ?? post.id })
            )}
          </li>
        )
      })}
    </Box>
  )
}

/** Итог отмены: сообщение сервера и отложенные записи VK, оставшиеся в VK */
export function SocialCancelResultAlert({ result, onClose }: { result: SocialCancelResponse; onClose: () => void }) {
  const hasPosts = result.vk_posts_to_delete.length > 0

  // Сообщение сервера уже говорит, сколько записей VK удалить вручную; ниже — их ссылки
  return (
    <Alert severity={hasPosts ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={onClose}>
      {result.message}
      {hasPosts && <VkPostLinks posts={result.vk_posts_to_delete} publication={result.data} />}
    </Alert>
  )
}

interface SocialCancelDialogProps {
  open: boolean
  publication: SocialPublication | null
  onClose: () => void
  onCancelled: (response: SocialCancelResponse) => void
}

/** Подтверждение отмены публикации; предупреждает об уже созданной отложенной записи VK */
export function SocialCancelDialog({ open, publication, onClose, onCancelled }: SocialCancelDialogProps) {
  const { t } = useTranslation()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const vkPosts = useMemo(() => (publication ? scheduledVkPosts(publication) : []), [publication])

  const handleClose = () => {
    if (!submitting) onClose()
  }

  const handleConfirm = async () => {
    if (!publication) return

    setSubmitting(true)
    setError(null)
    try {
      onCancelled(await socialApi.cancel(publication.id))
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('admin.social.actionFailed')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: () => setError(null) }}
    >
      <DialogTitle>{t('admin.social.cancelDialog.title')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography sx={{ mb: vkPosts.length > 0 ? 2 : 0 }}>{t('admin.social.cancelDialog.text')}</Typography>
        {vkPosts.length > 0 && (
          <Alert severity="warning">
            {t('admin.social.cancelDialog.vkWarning')}
            <VkPostLinks posts={vkPosts} publication={publication} />
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('common.close')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={submitting || !publication}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('admin.social.cancelDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
